import { findAncestorState } from '../common/ancestor';
import { createCalendarWidget } from '../common/calendar';
import { findModelAttribute } from '../common/model';
import { eventInsidePicker, setupPopover, setupTrigger } from '../common/picker-popover';
import { disabledControlClasses, pickerCellWrapperClasses, pickerFieldWrapperClasses, pickerWrapperClasses } from '../common/shared-classes';
import uuidv4 from '../utils/uuid';
import { sizeObserver } from './../common/input-size';

export default function (Alpine) {
  Alpine.directive('h-date-picker', (el, { original, modifiers }, { Alpine, cleanup }) => {
    const state = Alpine.reactive({
      expanded: false,
    });
    el._h_datepicker = {
      id: undefined,
      controls: `hdpc${uuidv4()}`,
      input: undefined,
      state,
      inTable: modifiers.includes('table'),
      range: false,
    };
    el._h_datepicker.input = el.querySelector('input');
    if (!el._h_datepicker.input || el._h_datepicker.input.tagName !== 'INPUT') {
      throw new Error(`${original} must contain an input`);
    } else if (el._h_datepicker.input.hasAttribute('id')) {
      el._h_datepicker.id = el._h_datepicker.input.getAttribute('id');
    } else {
      const id = `hdp${uuidv4()}`;
      el._h_datepicker.input.setAttribute('id', id);
      el._h_datepicker.id = id;
    }

    el.classList.add(...pickerWrapperClasses);
    if (el._h_datepicker.inTable) {
      el.classList.add(...pickerCellWrapperClasses);
      el.setAttribute('data-slot', 'cell-input-date');
    } else {
      el.classList.add(...pickerFieldWrapperClasses);
      el.setAttribute('data-slot', 'date-picker');
    }

    el._h_datepicker.input.classList.add(
      'bg-transparent',
      'outline-none',
      'size-full',
      'pr-1',
      'border-input',
      'aria-invalid:border-negative',
      'user-invalid:border-negative',
      '[[data-validate=immediate]_&:invalid]:border-negative',
      'focus-visible:ring-0',
      ...disabledControlClasses,
      'disabled:cursor-not-allowed',
      'md:text-sm',
      'text-base',
      'truncate'
    );
    if (el._h_datepicker.inTable) {
      // The input-to-trigger divider shows only when the table has horizontal borders.
      el._h_datepicker.input.classList.add('min-w-0', '[table[data-borders=rows]_&]:border-r', '[table[data-borders=both]_&]:border-r');
    } else {
      el._h_datepicker.input.classList.add('border-r');
    }
    el._h_datepicker.input.setAttribute('aria-autocomplete', 'none');
    el._h_datepicker.input.setAttribute('type', 'text');

    const observer = sizeObserver(el);

    cleanup(() => {
      observer.disconnect();
    });
  });

  Alpine.directive('h-date-picker-trigger', (el, { original }, { effect, cleanup, Alpine }) => {
    const datepicker = findAncestorState(Alpine, el, '_h_datepicker');
    if (!datepicker) {
      throw new Error(`${original} must be inside an date-picker element`);
    }
    setupTrigger(el, {
      pickerState: datepicker._h_datepicker,
      Alpine,
      effect,
      cleanup,
      original,
      slot: 'date-picker-trigger',
      // In range mode a click inside the picker (e.g. picking the range start)
      // must keep the popover open until the range is complete. Single mode is
      // unchanged: any click closes it.
      stayOpenInside: (event) => datepicker._h_datepicker.range && eventInsidePicker(datepicker, event),
    });
  });

  Alpine.directive('h-date-picker-popup', (el, { original, expression }, { effect, evaluateLater, cleanup, Alpine }) => {
    const datepicker = findAncestorState(Alpine, el, '_h_datepicker');
    if (!datepicker) {
      console.warn(`${original}: must be used inside an x-h-date-picker element`);
      return;
    }

    const { input } = datepicker._h_datepicker;

    el.setAttribute('tabindex', '-1');
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('data-slot', 'date-picker-calendar');

    const widget = createCalendarWidget('x-h-date-picker-popup', el, {
      Alpine,
      onSelectionChanged: (triggerInput) => {
        input.value = widget.formatSelectedDate() ?? '';
        input.setCustomValidity('');
        if (triggerInput) {
          input.dispatchEvent(new Event('change', { bubbles: true }));
          datepicker._h_datepicker.state.expanded = false;
        }
      },
      onEscape: () => {
        datepicker._h_datepicker.state.expanded = false;
      },
      onInvalidModel: () => input.setCustomValidity('Input value is not a valid date.'),
      onModelValid: () => {
        input.value = widget.formatSelectedDate() ?? '';
      },
      stopNavPropagation: true,
      tableFullWidth: false,
    });

    const onInputChange = (event) => {
      if (event && !event.isTrusted) return;
      if (input.value.trim() === '') {
        widget.clearSelectedAndSync();
        return;
      }
      const parsed = widget.parseDisplayValue(input.value);
      if (widget.isRange()) {
        const { start, end } = parsed;
        if (!start || isNaN(start) || (end && isNaN(end))) {
          console.error(`${original}: input value is not a valid date range - ${input.value}`);
          input.setCustomValidity('Input value is not a valid date range.');
          return;
        }
        const current = widget.getSelected();
        if (!widget.isSameDay(current.start, start) || !widget.isSameDay(current.end, end)) {
          widget.setSelectedAndSync({ start, end });
          widget.render();
        }
        input.setCustomValidity('');
        return;
      }
      if (isNaN(parsed)) {
        console.error(`${original}: input value is not a valid date - ${input.value}`);
        input.setCustomValidity('Input value is not a valid date.');
        return;
      }
      if (!widget.getSelected() || !widget.isSameDay(widget.getSelected(), parsed)) {
        widget.setSelectedAndSync(parsed);
        widget.render();
      }
      input.setCustomValidity('');
    };
    input.addEventListener('change', onInputChange);

    if (expression) {
      const getConfig = evaluateLater(expression);
      effect(() => {
        getConfig((config) => {
          widget.setConfig(config);
          datepicker._h_datepicker.range = widget.isRange();
        });
      });
    } else {
      widget.setConfig({});
      datepicker._h_datepicker.range = widget.isRange();
    }

    if (Object.prototype.hasOwnProperty.call(el, '_x_model')) {
      const modelExpression = findModelAttribute(Alpine, el)?.value;
      const evaluateModel = evaluateLater(modelExpression);

      effect(() => {
        evaluateModel((value) => {
          if (widget.applyModel(value)) {
            input.value = '';
            input.setCustomValidity('');
          }
        });
      });
    }

    setupPopover(el, {
      anchor: datepicker,
      pickerState: datepicker._h_datepicker,
      Alpine,
      effect,
      cleanup,
      onOpen: () => widget.focusDay(),
    });

    cleanup(() => {
      widget.cleanup();
      input.removeEventListener('change', onInputChange);
    });
  });
}
